import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Drawer,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RecipeDetails from './RecipeDetails';

// Traduction des jours de la semaine
const daysInFrench = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const mealTranslations = {
  breakfast: 'Petit-dejeuner',
  morningSnack: 'Encas matin',
  lunch: 'Dejeuner',
  snack: 'Gouter',
  dinner: 'Diner',
};

// Mapping des types de jours (normalisation)
const DAY_TYPE_CONFIG = {
  OFF: { label: 'Repos', color: 'default', bgColor: 'grey.100' },
  rest: { label: 'Repos', color: 'default', bgColor: 'grey.100' },
  ON: { label: 'Sport', color: 'primary', bgColor: 'primary.light' },
  sport: { label: 'Sport', color: 'primary', bgColor: 'primary.light' },
  INTENSIF: { label: 'Intense', color: 'error', bgColor: 'error.light' },
  active_rest: { label: 'Intense', color: 'error', bgColor: 'error.light' },
};

/**
 * DayPlans - Affichage des 7 jours de repas
 *
 * V3.0: Mobile-First Refactoring
 * - Grid responsive (1 col mobile, 2 cols tablet, auto-fit desktop)
 * - Drawer bottom sur mobile, right sur desktop
 * - Dates reelles si startDate fourni
 */
const DayPlans = ({ dayPlans, targetMacros, startDate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleRecipeClick = (meal) => {
    // Fusionner les donnees du meal et de la recette
    const recipeData = {
      ...meal.recipe,
      name: meal.name || meal.recipeName || meal.recipe?.name,
      macros: meal.macros || meal.recipe?.macros,
      ingredients: meal.ingredients || meal.recipe?.ingredients || [],
      imageUrl: meal.imageUrl || meal.recipe?.imageUrl,
      preparationTime: meal.preparationTime || meal.recipe?.preparationTime,
      cookingTime: meal.cookingTime || meal.recipe?.cookingTime,
      instructions: meal.instructions || meal.recipe?.instructions || '',
      instructionSteps: meal.instructionSteps || meal.recipe?.instructionSteps || [],
    };

    setSelectedRecipe(recipeData);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRecipe(null);
  };

  // Calcule les calories totales par jour
  const calculateTotalCalories = (dayPlan) => {
    let total = 0;
    for (const mealKey of ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner']) {
      const meal = dayPlan.meals?.[mealKey];
      if (meal) {
        const calories = meal.macros?.calories || meal.recipe?.macros?.calories || 0;
        total += calories;
      }
    }
    return total;
  };

  // Calcule la date pour chaque jour
  const getDayDate = (index) => {
    if (!startDate) return null;
    const date = new Date(startDate);
    date.setDate(date.getDate() + index);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Nombre de repas pour un jour
  const getMealCount = (dayPlan) => {
    let count = 0;
    for (const mealKey of ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner']) {
      if (dayPlan.meals?.[mealKey]) count++;
    }
    return count;
  };

  if (!dayPlans || dayPlans.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">Aucun repas planifie</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(auto-fit, minmax(280px, 1fr))',
          },
          gap: { xs: 2, sm: 2, md: 3 },
        }}
      >
        {dayPlans.map((dayPlan, index) => {
          const totalCalories = calculateTotalCalories(dayPlan);
          const dayTypeConfig = DAY_TYPE_CONFIG[dayPlan.dayType] || DAY_TYPE_CONFIG.OFF;
          const dayDate = getDayDate(index);
          const mealCount = getMealCount(dayPlan);

          return (
            <Card
              key={index}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: 'none',
                overflow: 'hidden',
              }}
            >
              {/* Header du jour */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: dayTypeConfig.bgColor,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {daysInFrench[index]}
                  </Typography>
                  {dayDate && (
                    <Typography variant="caption" color="text.secondary">
                      {dayDate}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Chip
                    label={dayTypeConfig.label}
                    color={dayTypeConfig.color}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                  <Chip
                    label={`${Math.round(totalCalories)} kcal`}
                    size="small"
                    variant="outlined"
                    sx={{ bgcolor: 'white' }}
                  />
                </Box>
              </Box>

              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                {/* Seance de sport */}
                {dayPlan.workoutSession && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.5,
                      mb: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <FitnessCenterIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        Seance du jour
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {dayPlan.workoutSession}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Liste des repas */}
                {mealCount === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Aucun repas planifie
                  </Typography>
                ) : (
                  <Box>
                    {['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner'].map((mealKey) => {
                      const meal = dayPlan.meals?.[mealKey];
                      if (!meal) return null;

                      const mealCalories = meal.macros?.calories || meal.recipe?.macros?.calories || 0;
                      const recipeName = meal.name || meal.recipeName || 'Recette';

                      return (
                        <Box
                          key={mealKey}
                          onClick={() => handleRecipeClick(meal)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1.5,
                            px: 1,
                            mx: -1,
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'grey.50',
                            },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {mealTranslations[mealKey]}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {recipeName}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(mealCalories)} kcal
                            </Typography>
                            <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Drawer pour les details de la recette */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 400,
            height: isMobile ? '90vh' : '100%',
            borderTopLeftRadius: isMobile ? 16 : 0,
            borderTopRightRadius: isMobile ? 16 : 0,
          },
        }}
      >
        <RecipeDetails
          recipe={selectedRecipe}
          targetMacros={targetMacros}
          open={drawerOpen}
          onClose={handleCloseDrawer}
          isMobile={isMobile}
        />
      </Drawer>
    </Box>
  );
};

export default DayPlans;
